import { useMemo, type RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import { ShieldCheck, Loader2, Clock } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { useForm, type FormContext, type FormField } from '@/shared/lib/forms'
import { disclaimerBlocks } from '@/shared/components/claims/disclaimer'
import type { Slot, Shortfall, DisclaimerConfig } from '../types'
import { useFinalise, ShortfallError } from '../hooks/use-finalise'
import { useSaveProgress } from '../hooks/use-save-progress'
import { toast } from 'sonner'
import { toastApiError } from '@/shared/lib/api-error'
import { fieldErrorsFromApi, scrollToEntityField } from '../lib/form-field-errors'

interface FinaliseSectionProps {
  token: string
  slots: Slot[]
  /** Tenant-authored evidence-upload disclaimer; gates submit when required (DL-46). */
  disclaimer?: DisclaimerConfig | null
  /**
   * Whether the disclaimer has been acknowledged. Owned by the parent
   * (`ActiveSession`) so the notice + confirmation render at the top of the
   * capture; this component only reads it to gate the submit button.
   */
  acknowledged?: boolean
  /**
   * Called when the user tries to submit with a required acknowledgement still
   * unticked, so the parent can surface the error border/message on the
   * top-of-page disclaimer.
   */
  onDisclaimerViolation?: () => void
  onShortfalls: (shortfalls: Shortfall[]) => void
  onSuccess: () => void
  onSaveProgress: (email: string) => void
  isDemo?: boolean
  getAttributeValues?: () => Record<string, unknown>
  hasEntityForm?: boolean
  entityFormRef?: RefObject<FormContext | null>
  /** A form photo is still uploading. */
  uploading?: boolean
}

const FORM_ID = 'iris-upload-finalise-form'

export function FinaliseSection({
  token,
  slots,
  disclaimer = null,
  acknowledged = false,
  onDisclaimerViolation,
  onShortfalls,
  onSuccess,
  onSaveProgress,
  isDemo,
  getAttributeValues,
  hasEntityForm = false,
  entityFormRef,
  uploading = false,
}: FinaliseSectionProps) {
  const { t } = useTranslation()
  const finalise = useFinalise(token, isDemo)
  const saveProgress = useSaveProgress(token, isDemo)
  const disclaimerUnmet = disclaimerBlocks(disclaimer, acknowledged)

  const requiredSlots = slots.filter((s) => s.min > 0)
  const allMet =
    requiredSlots.length === 0
      ? true
      : requiredSlots.every((s) => s.uploads.length >= s.min)

  const hasAnyUpload = slots.some((s) => s.uploads.length > 0)
  // A form photo still uploading is not in the answers yet; wait for it.
  const isBusy = finalise.isPending || saveProgress.isPending || uploading

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'email',
        name: 'uploader_email',
        label: 'Your email (optional)',
        placeholder: 'email@example.com',
        value: '',
        autoComplete: 'email',
      },
      {
        type: 'text',
        name: 'uploader_name',
        label: 'Your name (optional)',
        placeholder: 'Full name',
        value: '',
        autoComplete: 'name',
        maxLength: 200,
      },
    ],
    [],
  )

  const { Form: FinaliseForm, ref: formRef } = useForm(
    {
      name: FORM_ID,
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      // A required acknowledgement that's still unticked fails validation here
      // (the button stays enabled so the failure surfaces as an error on the
      // top-of-page disclaimer, rather than a silently disabled button).
      if (disclaimerUnmet) {
        onDisclaimerViolation?.()
        toast.error(t('upload.acknowledgeToSubmit'))
        return
      }
      const entityForm = entityFormRef?.current
      if (entityForm) {
        await entityForm.submitForm()
        const fieldErrors = await entityForm.validateForm()
        const firstCode = Object.keys(fieldErrors)[0]
        if (firstCode) {
          scrollToEntityField(firstCode)
          return
        }
      }
      const values = rawValues as { uploader_email: string; uploader_name: string }
      const email = values.uploader_email.trim()
      const name = values.uploader_name.trim()
      try {
        await finalise.mutateAsync({
          uploaderEmail: email || undefined,
          uploaderName: name || undefined,
          attributeValues: getAttributeValues?.(),
        })
        onSuccess()
      } catch (err) {
        if (err instanceof ShortfallError) {
          onShortfalls(err.shortfalls)
          toast.error('Some required items are still missing')
          return
        }
        const apiFieldErrors = fieldErrorsFromApi(err)
        const firstApiCode = Object.keys(apiFieldErrors)[0]
        if (entityForm && firstApiCode) {
          for (const [code, message] of Object.entries(apiFieldErrors)) {
            entityForm.setFieldError(code, message)
          }
          scrollToEntityField(firstApiCode)
          toast.error(t('upload.form.fillRequired', 'Fill in the required fields'))
          return
        }
        toastApiError(err, 'Something went wrong. Please try again.')
      }
    },
    [
      finalise,
      onShortfalls,
      onSuccess,
      disclaimerUnmet,
      onDisclaimerViolation,
      t,
      getAttributeValues,
      entityFormRef,
    ],
  )

  const handleSaveProgress = async () => {
    const values = (formRef.current?.values || {}) as {
      uploader_email?: string
      uploader_name?: string
    }
    const email = (values.uploader_email || '').trim()
    const name = (values.uploader_name || '').trim()
    try {
      await saveProgress.mutateAsync({
        uploaderEmail: email || undefined,
        uploaderName: name || undefined,
        attributeValues: getAttributeValues?.(),
      })
      onSaveProgress(email)
    } catch {
      toast.error('Could not save your progress. Please try again.')
    }
  }

  return (
    <div className="mt-6 rounded-lg border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] p-4 shadow-[var(--shadow-float)]">
      <h3 className="clara-heading mb-3 text-[18px]">
        {hasEntityForm
          ? t('upload.form.submitTitle', 'Submit form')
          : t('upload.submitEvidence', 'Submit Evidence')}
      </h3>

      <div className="mb-4">
        <FinaliseForm className="flex flex-col gap-3" />
      </div>

      <Button
        type="submit"
        form={FORM_ID}
        size="lg"
        className="h-12 w-full gap-2.5 bg-[var(--green)] text-[12px] tracking-[0.06em] text-white hover:bg-[var(--green-hover)]"
        // Kept enabled when the only thing missing is the acknowledgement, so
        // pressing it surfaces the error on the disclaimer instead of a
        // silently disabled button.
        disabled={!allMet || isBusy}
      >
        {finalise.isPending ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            Submitting...
          </>
        ) : uploading ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            {t('upload.form.uploading', 'Uploading photos...')}
          </>
        ) : (
          <>
            <ShieldCheck size={16} />
            {hasEntityForm
              ? t('upload.form.submit', 'Submit form')
              : t('upload.submitEvidence', 'Submit Evidence')}
          </>
        )}
      </Button>

      <Button
        type="button"
        variant="outline"
        size="lg"
        onClick={handleSaveProgress}
        disabled={isBusy}
        className="mt-2 h-11 w-full gap-2.5 text-[12px] tracking-[0.06em]"
      >
        {saveProgress.isPending ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <Clock size={16} />
            Save & finish later
          </>
        )}
      </Button>

      {!allMet && (
        <p className="clara-small mt-2 text-center text-[var(--ink-3)]">
          {hasAnyUpload
            ? "Complete all required items to submit, or save your progress and come back later"
            : 'Complete all required items to submit'}
        </p>
      )}

      {allMet && !isBusy && disclaimerUnmet && (
        <p className="clara-small mt-2 text-center text-[var(--ink-3)]">
          {t('upload.acknowledgeToSubmit')}
        </p>
      )}

      {allMet && !isBusy && !disclaimerUnmet && (
        <p className="clara-small mt-2 text-center text-[var(--ink-3)]">
          {hasEntityForm
            ? t('upload.form.reviewHint', 'Your answers go to the team that sent this form')
            : 'Your photos will be reviewed by the claims team'}
        </p>
      )}
    </div>
  )
}
